import React, { Component } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  Text,
  Icon,
  HStack,
  Progress
} from '@chakra-ui/react'
import { EditIcon, CheckCircleIcon, ChatIcon, EmailIcon } from '@chakra-ui/icons'
import colors from '../theme/colors'

class OnboardingTour extends Component {
  constructor(props) {
    super(props)
    this.state = {
      currentStep: 0
    }
  }

  steps = [
    {
      title: 'Welkom bij Demos! 👋',
      icon: '🎉',
      content: (
        <VStack spacing={4} align="start">
          <Text fontSize="lg">
            Hier kun je jouw ideeën en suggesties delen met je vertegenwoordigers.
          </Text>
          <Text>
            Zij lezen je inzendingen en kunnen erop reageren.
          </Text>
          <Text fontWeight="bold" color={colors.primary.main}>
            Laten we je snel rondleiden!
          </Text>
        </VStack>
      )
    },
    {
      title: 'Dien een Suggestie In',
      icon: EditIcon,
      iconColor: colors.primary.main,
      content: (
        <VStack spacing={4} align="start">
          <Text fontSize="lg" fontWeight="semibold">
            💡 Suggesties Indienen
          </Text>
          <Text>
            Klik op <strong>"Indienen"</strong> in de zijbalk om een nieuw idee te delen.
          </Text>
          <Text>
            Schrijf in het kort wat je voorstelt en waarom het belangrijk is.
          </Text>
          <Text color="gray.600" fontSize="sm">
            Je suggestie wordt doorgestuurd naar je vertegenwoordigers.
          </Text>
        </VStack>
      )
    },
    {
      title: 'Volg je Suggesties',
      icon: CheckCircleIcon,
      iconColor: 'green.500',
      content: (
        <VStack spacing={4} align="start">
          <Text fontSize="lg" fontWeight="semibold">
            📋 Je Ingediende Suggesties
          </Text>
          <Text>
            Onder <strong>Historie</strong> zie je al je eerdere inzendingen.
          </Text>
          <Text>
            Als een vertegenwoordiger je suggestie heeft gelezen, zie je dat hier ook terug.
          </Text>
          <Text color="gray.600" fontSize="sm">
            Zo blijf je op de hoogte van de voortgang.
          </Text>
        </VStack>
      )
    },
    {
      title: 'Berichten',
      icon: EmailIcon,
      iconColor: colors.primary.main,
      content: (
        <VStack spacing={4} align="start">
          <Text fontSize="lg" fontWeight="semibold">
            ✉️ Berichten van Vertegenwoordigers
          </Text>
          <Text>
            Bij <strong>"Berichten"</strong> ontvang je reacties van vertegenwoordigers op je suggesties.
          </Text>
          <Text>
            Zij kunnen je hier laten weten wat ze met je suggestie gaan doen of om meer informatie vragen.
          </Text>
          <Text color="gray.600" fontSize="sm">
            Zo blijf je in contact met je vertegenwoordigers.
          </Text>
        </VStack>
      )
    },
    {
      title: 'Hulp en Feedback',
      icon: ChatIcon,
      iconColor: colors.primary.main,
      content: (
        <VStack spacing={4} align="start">
          <Text fontSize="lg" fontWeight="semibold">
            💬 Heb je hulp nodig?
          </Text>
          <Text>
            Klik rechtsboven op het <strong>chat-icoontje</strong> om feedback over het platform te geven.
          </Text>
          <Text>
            Je kunt ook je privacy- en accountinstellingen bekijken bij <strong>"Instellingen"</strong>.
          </Text>
          <Text color={colors.primary.main} fontWeight="bold" mt={2}>
            Veel succes met je eerste suggestie! 🚀
          </Text>
        </VStack>
      )
    }
  ]

  handleNext = () => {
    const { currentStep } = this.state
    if (currentStep < this.steps.length - 1) {
      this.setState({ currentStep: currentStep + 1 })
    } else {
      // Last step - complete the tour
      this.handleComplete()
    }
  }

  handlePrevious = () => {
    const { currentStep } = this.state
    if (currentStep > 0) {
      this.setState({ currentStep: currentStep - 1 })
    }
  }

  handleSkip = () => {
    this.handleComplete()
  }

  handleComplete = () => {
    // Call the onComplete callback
    if (this.props.onComplete) {
      this.props.onComplete()
    }
  }

  render() {
    const { isOpen } = this.props
    const { currentStep } = this.state
    const step = this.steps[currentStep]
    const progress = ((currentStep + 1) / this.steps.length) * 100

    return (
      <Modal
        isOpen={isOpen}
        onClose={this.handleSkip}
        size="lg"
        closeOnOverlayClick={false}
        isCentered
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent>
          <ModalHeader>
            <VStack align="start" spacing={2}>
              <HStack>
                {typeof step.icon === 'string' ? (
                  <Text fontSize="2xl">{step.icon}</Text>
                ) : (
                  <Icon as={step.icon} boxSize={6} color={step.iconColor} />
                )}
                <Text>{step.title}</Text>
              </HStack>
              <Progress
                value={progress}
                size="xs"
                sx={{
                  '& > div': {
                    backgroundColor: colors.primary.main
                  }
                }}
                width="100%"
                borderRadius="md"
              />
              <Text fontSize="xs" color="gray.500">
                Stap {currentStep + 1} van {this.steps.length}
              </Text>
            </VStack>
          </ModalHeader>

          <ModalBody py={6}>
            {step.content}
          </ModalBody>

          <ModalFooter>
            <HStack spacing={3} width="100%" justify="space-between">
              <Button
                variant="ghost"
                onClick={this.handleSkip}
                size="sm"
              >
                Overslaan
              </Button>

              <HStack spacing={2}>
                {currentStep > 0 && (
                  <Button
                    variant="outline"
                    borderColor={colors.primary.main}
                    color={colors.primary.main}
                    _hover={{ bg: colors.primary.pale }}
                    onClick={this.handlePrevious}
                  >
                    ← Vorige
                  </Button>
                )}
                <Button
                  bg={colors.primary.main}
                  color="white"
                  _hover={{ bg: colors.primary.dark }}
                  onClick={this.handleNext}
                >
                  {currentStep < this.steps.length - 1 ? 'Volgende →' : 'Afronden ✓'}
                </Button>
              </HStack>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    )
  }
}

export default OnboardingTour
